import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

// biome-ignore lint/suspicious/noExplicitAny: Recharts formatter type compatibility
type AnyFormatter = any

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'

interface ChartDataPoint {
  date: string
  revenue: number
  cost: number
  margin: number
  tasks: number
  workflows: number
  outcomes: number
}

interface AgentMargin {
  agentName: string
  revenue: number
  cost: number
  margin: number
}

interface AgentRevenue {
  agentName: string
  value: number
  percentage: number
}

interface AnalyticsGridProps {
  chartData: ChartDataPoint[]
  marginByAgent: AgentMargin[]
  revenueByAgent: AgentRevenue[]
  loading?: boolean
}

export function AnalyticsGrid({
  chartData,
  marginByAgent,
  revenueByAgent,
  loading = false,
}: AnalyticsGridProps) {
  const COLORS = [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
  ]

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}k`
    }
    return `$${amount.toLocaleString()}`
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}k`
    }
    return num.toLocaleString()
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  if (loading) {
    return (
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {Array.from({ length: 4 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          <Card key={i} className='bg-transparent border'>
            <CardHeader>
              <div className='animate-pulse h-6 bg-slate-300 dark:bg-slate-600 rounded w-32'></div>
            </CardHeader>
            <CardContent>
              <div className='animate-pulse h-64 bg-slate-300 dark:bg-slate-600 rounded'></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
      {/* Revenue vs Cost Trends */}
      <Card className='bg-transparent border'>
        <CardHeader>
          <CardTitle className='text-lg'>Revenue vs Cost Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width='100%' height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='date'
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={value => formatCurrency(value)}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={value => formatDate(value as string)}
                formatter={
                  ((value: number, name: string) => [
                    formatCurrency(value),
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]) as AnyFormatter
                }
              />
              <Legend />
              <Area
                type='monotone'
                dataKey='revenue'
                stackId='1'
                stroke='#10b981'
                fill='#10b981'
                fillOpacity={0.6}
                name='Revenue'
              />
              <Area
                type='monotone'
                dataKey='cost'
                stackId='2'
                stroke='#ef4444'
                fill='#ef4444'
                fillOpacity={0.6}
                name='Cost'
              />
              <Line
                type='monotone'
                dataKey='margin'
                stroke='#8b5cf6'
                strokeWidth={3}
                name='Margin'
                dot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Activity Volume Trends */}
      <Card className='bg-transparent border'>
        <CardHeader>
          <CardTitle className='text-lg'>Activity Volume Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width='100%' height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='date'
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={value => formatDate(value as string)}
                formatter={
                  ((value: number, name: string) => [
                    formatNumber(value),
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]) as AnyFormatter
                }
              />
              <Legend />
              <Line
                type='monotone'
                dataKey='tasks'
                stroke='#3b82f6'
                strokeWidth={2}
                name='Tasks'
              />
              <Line
                type='monotone'
                dataKey='workflows'
                stroke='#8b5cf6'
                strokeWidth={2}
                name='Workflows'
              />
              <Line
                type='monotone'
                dataKey='outcomes'
                stroke='#10b981'
                strokeWidth={2}
                name='Outcomes'
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Margin by Agent */}
      <Card className='bg-transparent border'>
        <CardHeader>
          <CardTitle className='text-lg'>Margin by Agent</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width='100%' height={300}>
            <BarChart data={marginByAgent}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='agentName'
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor='end'
                height={80}
              />
              <YAxis
                tickFormatter={value => formatCurrency(value)}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={
                  ((value: number, name: string) => [
                    formatCurrency(value),
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]) as AnyFormatter
                }
              />
              <Bar dataKey='revenue' fill='#10b981' name='Revenue' />
              <Bar dataKey='cost' fill='#ef4444' name='Cost' />
              <Bar dataKey='margin' fill='#3b82f6' name='Margin' />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Revenue by Agent */}
      <Card className='bg-transparent border'>
        <CardHeader>
          <CardTitle className='text-lg'>Revenue by Agent</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width='100%' height={300}>
            <PieChart>
              <Pie
                data={revenueByAgent}
                cx='50%'
                cy='50%'
                labelLine={false}
                label={
                  (({ agentName, percentage }: Record<string, unknown>) =>
                    `${agentName}: ${(percentage as number).toFixed(1)}%`) as never
                }
                outerRadius={90}
                fill='#8884d8'
                dataKey='value'
              >
                {revenueByAgent.map((_, index) => (
                  <Cell
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={
                  ((value: number) => [
                    formatCurrency(value),
                    'Revenue',
                  ]) as AnyFormatter
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  )
}
